import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import EmailAddress from "_shared/email-addresses/email-address";
import EmailAddressSchema from "_shared/email-addresses/email-address-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import Name from "_shared/names/name";
import NameSchema from "_shared/names/name-schema";
import TitleSchema from "_shared/titles/_schema/title-schema";
import Title from "_shared/titles/title";
import UserOpenEntityAgreementSchema from "legal-entities/agreements/open/_schema/user-open-entity-agreement-schema";
import UserOpenEntityAgreement from "legal-entities/agreements/open/user-open-entity-agreement";
import { UserOnboardingWorkSchema } from "users/information/user-onboarding-work";
import { z } from "zod";

export type UserInformationJSON = z.infer<typeof UserInformationSchema.UserInformationJSONZ>;
export type UserInformationParams = z.infer<typeof UserInformationSchema.UserInformationZ>;

export default class UserInformationSchema {
  public static readonly UserInformationJSONZ = z.object({
    id: z.uuid(),
    isSuperUser: z.boolean().nullish(),
    hasSigningAuthority: z.boolean().nullish(),
    hasEntityWideNotificationsAccess: z.boolean().nullish(),
    hasMemberPermissionsAccess: z.boolean().nullish(),
    viewingAsVendor: z.boolean().nullish(),
    email: z.email().nullish().or(z.literal("")),
    firstName: z.string(),
    lastName: z.string(),
    title: z.string().nullish(),
    openEntityAgreements: z.array(UserOpenEntityAgreementSchema.UserOpenEntityAgreementJSONZ).nullish(),
    hasOpenEntityAgreements: z.boolean().nullish(),
    individualId: z.uuid().nullish(),
    connectionId: z.uuid().or(z.literal(Guid.EMPTY.value)).nullish(),
    createdDate: z.iso.datetime({ offset: true }).nullish(),
    companyId: z.uuid().nullish(),
    legalEntityId: z.uuid(),
    companyName: z.string().nullish(),
    companyProfileVisible: z.boolean().nullish(),
    avatarId: z.uuid().nullish(),
    teamsOn: z.number().nullish(),
    teamsLeading: z.number().nullish(),
    myComments: z.string().nullish(),
    onboardingWorkId: z.uuid().nullish(),
    onboardingWork: UserOnboardingWorkSchema.nullish(),
  });

  public static readonly UserInformationZ = z.object({
    id: z.instanceof(Guid),
    isSuperUser: z.boolean().optional(),
    hasSigningAuthority: z.boolean().optional(),
    hasEntityWideNotificationsAccess: z.boolean().optional(),
    hasMemberPermissionsAccess: z.boolean().optional(),
    viewingAsVendor: z.boolean().optional(),
    email: z.instanceof(EmailAddress).optional(),
    name: z.instanceof(Name),
    title: z.instanceof(Title).optional(),
    openEntityAgreements: z.array(z.instanceof(UserOpenEntityAgreement)).optional(),
    hasOpenEntityAgreements: z.boolean().optional(),
    individualId: z.instanceof(Guid).optional(),
    connectionId: z.instanceof(Guid).optional(),
    createdDate: z.instanceof(AHDate).optional(),
    companyId: z.instanceof(Guid).optional(),
    legalEntityId: z.instanceof(Guid),
    companyName: z.string().nullish(),
    companyProfileVisible: z.boolean().optional(),
    avatarId: z.instanceof(Guid).optional(),
    teamsOn: z.number().optional(),
    teamsLeading: z.number().optional(),
    myComments: z.string().optional(),
    onboardingWorkId: z.instanceof(Guid).optional(),
    onboardingWork: UserOnboardingWorkSchema.optional(),
  });

  public static validate(values: unknown): UserInformationParams {
    return parseOrThrow(UserInformationSchema.UserInformationZ, values, {
      logError: true,
      schemaName: "UserInformationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserInformationJSON {
    return parseOrThrow(UserInformationSchema.UserInformationJSONZ, json, {
      logError: true,
      schemaName: "UserInformationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserInformationParams {
    try {
      UserInformationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserInformationJSON {
    try {
      UserInformationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): UserInformationParams {
    const parsed = parseOrThrow(UserInformationSchema.UserInformationJSONZ, json, {
      logError: true,
      schemaName: "UserInformationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserInformationSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: UserInformationParams): UserInformationJSON {
    const parsed = UserInformationSchema.validate(legalEntity);
    return UserInformationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    UserInformationSchema.UserInformationJSONZ,
    UserInformationSchema.UserInformationZ,
    {
      decode: (data) => ({
        id: new Guid(GuidSchema.deserialize(data.id)),
        email: data.email?.length ? new EmailAddress(EmailAddressSchema.deserialize(data.email)) : undefined,
        name: new Name(NameSchema.deserialize(data)),
        title: data.title ? new Title(TitleSchema.deserialize(data.title)) : undefined,
        legalEntityId: new Guid(GuidSchema.deserialize(data.legalEntityId)),
        openEntityAgreements: UserOpenEntityAgreement.fromJSONArray(data.openEntityAgreements) ?? [],
        hasOpenEntityAgreements: data.hasOpenEntityAgreements ?? false,
        hasSigningAuthority: data.hasSigningAuthority ?? false,
        hasEntityWideNotificationsAccess: data.hasEntityWideNotificationsAccess ?? false,
        hasMemberPermissionsAccess: data.hasMemberPermissionsAccess ?? false,
        viewingAsVendor: data.viewingAsVendor ?? false,
        individualId: Guid.fromJSON(data.individualId) ?? undefined,
        isSuperUser: data.isSuperUser ?? false,
        avatarId: Guid.fromJSON(data.avatarId) ?? undefined,
        connectionId: Guid.fromJSON(data.connectionId) ?? undefined,
        createdDate: AHDate.fromJSON(data.createdDate) ?? undefined,
        companyId: Guid.fromJSON(data.companyId) ?? undefined,
        companyName: data.companyName,
        CompanyProfileVisible: data.companyProfileVisible ?? undefined,
        teamsOn: data.teamsOn ?? 0,
        teamsLeading: data.teamsLeading ?? 0,
        myComments: data.myComments ?? "",
        onboardingWorkId: Guid.fromJSON(data.onboardingWorkId) ?? undefined,
        onboardingWork: data.onboardingWork ?? undefined,
        companyProfileVisible: data.companyProfileVisible ?? undefined,
      }),
      encode: (entity) => {
        const { firstName, lastName } = entity.name?.toJSON() ?? {};
        return {
          id: entity.id?.toJSON(),
          isSuperUser: entity.isSuperUser,
          hasSigningAuthority: entity.hasSigningAuthority,
          hasEntityWideNotificationsAccess: entity.hasEntityWideNotificationsAccess,
          hasMemberPermissionsAccess: entity.hasMemberPermissionsAccess,
          email: EmailAddressSchema.validateJSON(entity.email?.toJSON()),
          firstName: z
            .string()
            .min(1)
            .encode(firstName ?? ""),
          lastName: z
            .string()
            .min(1)
            .encode(lastName ?? ""),
          title: z.string().encode(entity.title?.toJSON() ?? ""),
          legalEntityId: entity.legalEntityId?.toJSON(),
          openEntityAgreements: entity.openEntityAgreements?.map((agreement) => agreement.toJSON()) ?? [],
          hasOpenEntityAgreements: entity.hasOpenEntityAgreements,
          individualId: entity.individualId?.toJSON(),
          connectionId: entity.connectionId?.toJSON(),
          createdDate: entity.createdDate?.toJSON(),
          companyId: entity.companyId?.toJSON(),
          companyName: entity.companyName,
          companyProfileVisible: entity.companyProfileVisible,
          teamsOn: entity.teamsOn,
          teamsLeading: entity.teamsLeading,
          myComments: entity.myComments,
          onboardingWorkId: entity.onboardingWorkId?.toJSON(),
          onboardingWork: entity.onboardingWork,
        };
      },
    },
  );
}
